import React, { useState, useCallback, useMemo } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import { styles } from "./style";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { getLeadsOptimized, getMarketsOptimized } from "redux/selector";
import { Item } from "./item";
import { getFuse } from "./items";
import { getUserUnsafe } from "redux/selector";
import { getActiveDripCampaigns } from "redux/selector";
import { getAgents } from "redux/selector";
import { getOffices } from "redux/selector";
import { FeatherIcon } from "component/shared/feather-icon";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { Dialog } from "component/shared/dialog";
import { getViewType } from "redux/selector";

interface Props extends Router.RouteComponentProps, WithStyles<typeof styles> {
	open: boolean;
	onClose: Function;
}

const Component: React.FC<Props> = ({ open, onClose, classes = {}, history }) => {
	// Redux selectors
	const user = useSelector((state: RootState) => getUserUnsafe(state));
	const viewType = useSelector((state: RootState) => getViewType(state));
	const leads = useSelector((state: RootState) => getLeadsOptimized(state));
	const campaigns = useSelector((state: RootState) => getActiveDripCampaigns(state));
	const markets = useSelector((state: RootState) => getMarketsOptimized(state));
	const agents = useSelector((state: RootState) => getAgents(state));
	const offices = useSelector((state: RootState) => getOffices(state));

	// Local state
	const [term, setTerm] = useState("");
	const [selectedIndex, setSelectedIndex] = useState(0);

	// Check user permissions
	if (!user) {
		throw new Error();
	}

	// Memoized values
	const leadsPermitted = useMemo(() => 
		(user.permissions.broker && user.permissions.leads && user.ownsLeads) || 
		(!user.permissions.broker && user.permissions.leads) || 
		(user.admin && user.permissions.broker && user.permissions.leads)
	, [user.permissions.broker, user.permissions.leads, user.ownsLeads, user.admin]);

	const fuse = useMemo(() => getFuse(
		user,
		viewType,
		leadsPermitted ? leads : [],
		user.admin ? campaigns : [],
		user.admin ? markets : [],
		user.admin ? agents : [],
		user.admin ? offices : [],
	), [user, viewType, leadsPermitted, leads, campaigns, markets, agents, offices]);

	const items = useMemo(() => 
		fuse.search(term, { limit: 6 }).map((result: any) => result.item)
	, [fuse, term]);

	// Callback functions
	const handleTermChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setTerm(event.target.value);
		setSelectedIndex(0);
	}, []);

	const handleKeyDown = useCallback((event: React.KeyboardEvent, items: Item[]) => {
		let newSelectedIndex = selectedIndex;
		const up = event.keyCode === 38;
		const down = event.keyCode === 40 || event.keyCode === 9;
		const enter = event.keyCode === 13;
		
		if (up || down || enter) {
			event.preventDefault();
			if (up || down) {
				if (up) {
					newSelectedIndex--;
				}
				if (down) {
					newSelectedIndex++;
				}
				if (newSelectedIndex < 0) {
					newSelectedIndex = items.length - 1;
				}
				if (newSelectedIndex > items.length - 1) {
					newSelectedIndex = 0;
				}
				setSelectedIndex(newSelectedIndex);
			}
			if (enter) {
				const item = items[newSelectedIndex];
				if (item) {
					history.push(item.url);
					onClose();
				}
			}
		}
	}, [selectedIndex, history, onClose]);

	const handleClose = useCallback(() => {
		onClose();
	}, [onClose]);

	const placeholder = "Enter a page, lead, or other search term.";

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullWidth={true}
			maxWidth="sm"
			classes={{
				container: classes.container,
			}}
		>
			<Mui.DialogTitle style={{backgroundColor: "initial"}}>
				<Mui.TextField
					autoFocus
					label={term && placeholder}
					placeholder={placeholder}
					value={term}
					style={{ width: "calc(100% - 36px)" }}
					InputProps={{
						startAdornment: (
							<Mui.InputAdornment position="start">
								<FeatherIcon>
									<Icons.Search />
								</FeatherIcon>
							</Mui.InputAdornment>
						),
					}}
					onChange={handleTermChange}
					onKeyDown={(event) => handleKeyDown(event, items)}
				/>
				<Mui.IconButton
					className={classes.closeButton}  
					onClick={handleClose}
				>
					<FeatherIcon>
						<Icons.X />
					</FeatherIcon>
				</Mui.IconButton>
			</Mui.DialogTitle>
			{term && 
				<Mui.DialogContent dividers>
					{!!items.length &&
						<Mui.List>
							{items.map((item: Item, index: number) => (
								<Mui.ListItem
									key={index}
									button
									component={RouterLinkWrapper}
									to={item.url}
									className={item.className}
									selected={selectedIndex === index}
								>
									<Mui.ListItemAvatar>
										{item.line2 === "Lead" ? (
											item.icon
										) : (
											<Mui.Avatar>
												<FeatherIcon>
													{item.icon}
												</FeatherIcon>
											</Mui.Avatar>
										)}
									</Mui.ListItemAvatar>
									<Mui.ListItemText primary={item.line1} secondary={item.line2} />
								</Mui.ListItem>
							))}
						</Mui.List>
					}
					{!items.length &&
						<Mui.DialogContentText>No results</Mui.DialogContentText>
					}
				</Mui.DialogContent>
			}
			<Mui.DialogActions className={classes.actions}>
				<span className={classes.action}>
					<span className={classes.actionIcon}>↑↓</span>
					to navigate
				</span>
				<span className={classes.action}>
					<span className={classes.actionIcon}>↩</span>
					to select
				</span>
				<span className={classes.action}>
					<span className={classes.actionIcon}>esc</span>
					to dismiss
				</span>
			</Mui.DialogActions>
		</Dialog>
	);
};

export const QuickSearchDialog = withStyles(Router.withRouter(Component), styles);
